import { Trans, useLingui } from '@lingui/react/macro';
import { useMemo } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';

/**
 * Route-level `ErrorBoundary` (wired onto the root route in `@/routes`). Reads
 * the thrown error from `useRouteError()` and renders {@link RootErrorBoundary},
 * so any uncaught render error in the app shows a friendly fallback instead of
 * react-router's default "Unexpected Application Error" screen — important when
 * the library is embedded in a host (e.g. WFM).
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  return <RootErrorBoundary error={error} />;
}

/**
 * Root-level error UI (D38 — A8). Renders the error, a short correlation ID,
 * and a "Copy report" button. Used via {@link RouteErrorBoundary} as the route
 * error element; also rendered directly in unit tests.
 */
export function RootErrorBoundary({ error }: { error: unknown }) {
  const { t } = useLingui();
  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : t`Unknown error`;

  const stackFrame =
    error instanceof Error && error.stack ? (error.stack.split('\n')[1]?.trim() ?? '') : '';

  // Pinned to the error instance: re-renders for unrelated reasons (theme
  // toggle, language change, parent re-render) must not mutate the ID
  // shown to the user or copied into the support report.
  const correlationId = useMemo(
    () => btoa(`${Date.now()}|${message}|${stackFrame}`).slice(0, 16),
    [message, stackFrame],
  );

  function copyReport() {
    const payload = `correlationId=${correlationId}\nmessage=${message}\nframe=${stackFrame}`;
    void navigator.clipboard.writeText(payload);
  }

  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="bg-card max-w-lg space-y-4 rounded-lg border p-6">
        <h1 className="text-destructive text-[length:var(--section-title-font-size)] leading-[var(--section-title-line-height)] font-semibold">
          <Trans>Something went wrong</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">{message}</p>
        <p className="text-muted-foreground font-mono text-xs">
          <Trans>id: {correlationId}</Trans>
        </p>
        <button
          type="button"
          onClick={copyReport}
          className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm"
        >
          <Trans>Copy report to clipboard</Trans>
        </button>
      </div>
    </div>
  );
}
